import { Link } from 'react-router-dom';
import { XIcon, LinkedInIcon, InstagramIcon } from '@/components/icons/SocialIcons';
import { siteConfig } from '@/lib/siteConfig';

export const Footer = () => {
  return (
    <footer className="bg-[#0B2240] text-white" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-5">
            <Link to="/" className="inline-flex items-center group" data-testid="footer-logo">
              <img 
                src="/brand/kgob-logo-dark.svg" 
                alt="Kohari Gonzalez Oneyear & Brown CPAs & Advisors" 
                className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </Link>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#D4AF37]/85 pt-1" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
              {siteConfig.tagline}
            </p>
            <p className="text-sm text-white/70 font-serif italic leading-relaxed">
              Luxury Advisory. Precision Compliance.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-white mb-4">Contact</h3>
            <address className="not-italic text-sm text-white/70 space-y-2">
              <div>{siteConfig.address.line1}</div>
              <div>
                {siteConfig.address.city}, {siteConfig.address.state} {siteConfig.address.zip}
              </div>
              <div className="pt-2">
                <a data-testid="footer-a-4" 
                  href={`tel:${siteConfig.phone}`} 
                  className="hover:text-[#D4AF37] transition-colors block"
                >
                  {siteConfig.phoneDisplay}
                </a>
                <a data-testid="footer-a-3" 
                  href={`mailto:${siteConfig.email}`}
                  className="hover:text-[#D4AF37] transition-colors block"
                >
                  {siteConfig.email}
                </a>
              </div>
            </address>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link data-testid="footer-link-4" to="/#difference" className="hover:text-white transition-colors">Our Difference</Link></li>
              <li><Link data-testid="footer-link-3" to="/#innovation" className="hover:text-white transition-colors">Innovation</Link></li>
              <li><a data-testid="footer-a-2" href="https://ifyouteachme.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">KGOB Cares</a></li>
              <li>
                <Link
                  to="/journal"
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors group"
                  data-testid="footer-journal-link"
                >
                  <svg className="h-3 w-3 text-[#D4AF37] opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                  The Journal
                </Link>
              </li>
              <li>
                <Link
                  to="/locations"
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors group"
                  data-testid="footer-locations-link"
                >
                  <svg className="h-3 w-3 text-[#D4AF37] opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Locations
                </Link>
              </li>
              <li><Link data-testid="footer-link-2" to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="font-semibold text-white mb-4">Connect</h3>
            <ul className="space-y-2 text-sm text-white/70 mb-4">
              <li><Link to="/welcome" className="hover:text-white transition-colors text-[#D4AF37]/90 font-medium" data-testid="footer-become-client-link">Apply to become a client</Link></li>
              <li><Link data-testid="footer-link-1" to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/sign-in" className="hover:text-white transition-colors" data-testid="footer-sign-in-link">Sign In</Link></li>
              <li><a href="mailto:connect@kgob.com?subject=Careers%20at%20KGOB" className="hover:text-white transition-colors" data-testid="footer-careers-link">Careers</a></li>
            </ul>
            <div className="flex space-x-4">
              <a 
                href={siteConfig.socials.linkedin} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-[#2F81A0] transition-colors" 
                data-testid="footer-linkedin-link"
                aria-label="LinkedIn"
              >
                <LinkedInIcon className="w-5 h-5" />
              </a>
              <a 
                href={siteConfig.socials.x}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-white transition-colors" 
                data-testid="footer-twitter-link"
                aria-label="X (Twitter)"
              >
                <XIcon className="w-5 h-5" />
              </a>
              <a 
                href={siteConfig.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-[#A02F9B] transition-colors" 
                data-testid="footer-instagram-link"
                aria-label="Instagram"
              >
                <InstagramIcon className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-white/60">
            <p>&copy; {new Date().getFullYear()} {siteConfig.firm}. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a data-testid="footer-a-1" href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a data-testid="footer-a-1" href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="/email-preferences" className="hover:text-white transition-colors" data-testid="footer-email-preferences">Manage Email Preferences</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};